import { useCallback, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import supabase from 'lib/supabase-browser'
import {
  ChatbotMessage,
  useChatbotSettingsStore,
  chatbotFormSchema,
} from 'store/useChatbotSettingsStore'
import { Chatbot } from 'lib/types/generated-types'
import { useChatIconPresignedUrls } from 'hooks/useChatIconPresignedUrl'

// Helper function to load messages from localStorage
const loadMessagesFromLocalStorage = (
  chatbotId: string
): ChatbotMessage[] | null => {
  if (typeof window !== 'undefined') {
    try {
      const savedMessages = localStorage.getItem(
        `chatbot_messages_${chatbotId}`
      )
      return savedMessages ? JSON.parse(savedMessages) : null
    } catch (error) {
      console.error('Failed to load messages from localStorage:', error)
      return null
    }
  }
  return null
}

export const useChatBot = ({ id }: { id: string }) => {
  const {
    setChatbot,
    setField,
    setIsLoading,
    chatbot,
    setMessages,
    setIsVisible,
    setSuggestedMessages,
  } = useChatbotSettingsStore()

  const methods = useForm({
    resolver: zodResolver(chatbotFormSchema),
    // Remove defaultValues from here
  })

  // Reset form when chatbot data changes
  useEffect(() => {
    if (chatbot) {
      methods.reset(chatbot)
    }
  }, [chatbot, methods])

  // Move watch inside useEffect and prevent unnecessary updates
  useEffect(() => {
    const subscription = methods.watch((value, { name }) => {
      if (name) {
        const newValue = value[name]
        const currentValue = chatbot[name]
        if (newValue !== currentValue) {
          setField(name as keyof Chatbot, newValue)
        }
      }
      // Handle starter_messages and suggested_messages if they change
      if (name === 'starter_messages' && value[name] !== undefined) {
        setMessages(
          (value[name] ?? [])
            .filter((message) => message !== undefined)
            .map((message) => ({
              role: 'assistant',
              content: message as string,
            }))
        )
      }
      if (name === 'suggested_messages' && value[name] !== undefined) {
        setSuggestedMessages(
          (value[name] ?? []).filter((msg) => msg !== undefined) as string[]
        )
      }
    })
    return () => subscription.unsubscribe()
  }, [methods, chatbot, setField, setMessages, setSuggestedMessages])

  // Ensure useQuery options are stable
  const fetchChatbot = useCallback(async () => {
    setIsLoading(true)
    const { data, error } = await supabase
      .from('chatbots')
      .select('*')
      .eq('id', id)
      .single()

    if (error) {
      console.error('Error fetching chatbot:', error.message)
      throw new Error(error.message)
    }

    return data
  }, [id, setIsLoading])

  const query = useQuery(['chatbot', id], fetchChatbot, {
    enabled: !!id && !chatbot.id,
    onSuccess: (data) => {
      setChatbot(data)

      // Try to load messages from localStorage first
      const savedMessages = loadMessagesFromLocalStorage(id)
      if (savedMessages && savedMessages.length > 0) {
        setMessages(savedMessages)
      } else {
        // If no saved messages, initialize with starter messages
        setMessages(
          (data.starter_messages ?? []).map((message) => ({
            role: 'assistant',
            content: message,
          }))
        )
      }
    },
    onSettled: () => {
      setIsLoading(false)
      setIsVisible(true)
    },
    onError: (error) => {
      console.error('Error fetching chatbot:', error)
      setIsLoading(false)
      setIsVisible(false)
    },
  })

  const { iconLoading, bubbleIconLoading } = useChatIconPresignedUrls(
    chatbot as Chatbot
  )

  return { ...query, methods, iconLoading, bubbleIconLoading }
}
